import type {
  ChangeEvent,
  ComponentPropsWithoutRef,
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  RefObject,
} from 'react'
import React, { useEffect, useState } from 'react'

import classNames from 'classnames'

import { AnimationWrapper } from '../AnimationWrapper'
import { TickIcon } from '../Icons/Navigational/Tick'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './CheckBox.module.scss'

export const Checkbox: FunctionComponent<Props> = ({
  ariaLabel,
  checked = false,
  className,
  disabled = false,
  frozen = false, // functionality is frozen when tooltip error occurs (e.g. compare 5 products)
  allowEnterKey = false, // pressing enter triggers onChange (e.g. instead of following link to product page)
  id,
  label,
  labelChildren,
  name,
  onChangeCallback,
  value,
  inputRef,
  ...rest
}) => {
  const [isChecked, toggleIsChecked] = useState(checked)

  useEffect(() => {
    toggleIsChecked(checked)
  }, [checked])

  const onChangeHandler = (
    event:
      | React.ChangeEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | React.MouseEvent<SVGSVGElement>
  ) => {
    if (!disabled) {
      if (!frozen) {
        toggleIsChecked((prev) => !prev)
      }
      onChangeCallback({ id, value, event })
    }
  }

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
    if (allowEnterKey && event.code === 'Enter') {
      onChangeHandler(event)
    }
  }

  return (
    <div
      className={classNames(styles.checkBoxWrapper, className)}
      data-testid="checkbox"
    >
      <div
        className={classNames(styles.checkBoxItem, {
          [styles.checkBoxItemDisabled]: disabled,
        })}
      >
        {isChecked && (
          <TickIcon
            className={classNames(styles.checkBoxTickIcon, {
              [styles.checkBoxTickIconDisabled]: disabled,
            })}
            data-testid="checkbox-icon"
            onClick={onChangeHandler}
            {...{ disabled }}
          />
        )}
        <input
          aria-label={ariaLabel}
          checked={isChecked}
          className={classNames(styles.checkBox, {
            [styles.checkBoxDisabled]: disabled,
          })}
          data-testid="checkbox-input"
          onChange={onChangeHandler}
          onKeyDown={onKeyDownHandler}
          type="checkbox"
          ref={inputRef}
          {...{
            disabled,
            id,
            name,
            value,
            ...rest,
          }}
        />
        <TypographyV2
          textStyle="sb-text-interface-body-small-regular"
          className={classNames(styles.checkBoxLabel, {
            [styles.checkBoxLabelDisabled]: disabled,
          })}
          tag="label"
          aria-hidden={ariaLabel ? true : false}
          htmlFor={id}
        >
          {labelChildren ||
            (disabled ? (
              label
            ) : (
              <AnimationWrapper inverted>{label}</AnimationWrapper>
            ))}
        </TypographyV2>
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////

type ChangeEventType =
  | ChangeEvent<HTMLElement>
  | KeyboardEvent<HTMLElement>
  | MouseEvent<SVGSVGElement>

type Props = {
  id: string
  label: string
  name: string
  onChangeCallback: ({
    id,
    value,
    event,
  }: {
    id?: string
    value?: string
    event: ChangeEventType
  }) => void
  value: string
  ariaLabel?: string
  checked?: boolean
  className?: string
  disabled?: boolean
  frozen?: boolean
  allowEnterKey?: boolean
  labelChildren?: ReactElement
  inputRef?: RefObject<HTMLInputElement>
} & ComponentPropsWithoutRef<'input'>
